/**
 * Waarheidskaarten (sessie 61): wat Nova en Sanne over voeding en
 * gezondheid mogen zeggen, één kaart per onderwerp.
 *
 * Alleen een goedgekeurde kaart bestaat voor de motor. Concept en uit
 * staan hier wel, zodat JP ze kan lezen, bewerken en goedkeuren.
 *
 * Drie regels die dit scherm draagt:
 *
 * 1. **Opslaan schrijft direct weg.** Geen concept in de browser dat
 *    een andere stand laat zien dan de database.
 * 2. **Een knop die niet kan, zegt waarom.** Een 403 (geen beheerder)
 *    of een andere fout blijft bij de kaart in beeld staan tot de
 *    volgende poging; geen toast die na drie tellen weg is.
 * 3. **Eerst lezen, dan goedkeuren.** De knoppen staan in het
 *    uitgeklapte deel, onder de klanttekst.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useId, useState } from "react";

import { vertaalApiFout } from "@/lib/api";
import {
  werkplekKennisApi,
  type Waarheidskaart,
  type WaarheidskaartMag,
  type WaarheidskaartStatus,
  type WaarheidskaartWijziging,
} from "@/lib/werkplek-api";

const KAARTEN_SLEUTEL = ["werkplek", "kennis", "waarheidskaarten"];

const STATUSCHIP: Record<WaarheidskaartStatus, string> = {
  concept: "wp-chip wp-chip-mail",
  goedgekeurd: "wp-chip wp-chip-whatsapp",
  uit: "wp-chip wp-chip-grijs",
};

const MAG_OPTIES: { waarde: WaarheidskaartMag; label: string }[] = [
  { waarde: "uitleggen", label: "Mag uitleggen" },
  { waarde: "adviseren", label: "Mag adviseren" },
  { waarde: "waarschuwen", label: "Mag waarschuwen" },
];

const MAG_LABEL: Record<string, string> = Object.fromEntries(
  MAG_OPTIES.map((optie) => [optie.waarde, optie.label]),
);

/**
 * Vraagtypen die een kaart nu al heeft (seed 0192). De rest komt uit
 * het stoplicht, dat dezelfde vraagtypen-tabel leest.
 */
const VASTE_VRAAGTYPEN = ["voedingsadvies", "productvraag"];

const REDEN_GEEN_BEHEERDER =
  "Alleen een beheerder mag waarheidskaarten goedkeuren, uitzetten of bewerken.";

/** `klacht_kwaliteit` wordt "Klacht kwaliteit" als er geen naam is. */
function sleutelAlsNaam(sleutel: string): string {
  const tekst = sleutel.replace(/_/g, " ");
  return tekst.charAt(0).toUpperCase() + tekst.slice(1);
}

/** "25 september 2026", in Amsterdamse tijd. */
function datumLang(iso: string | null | undefined): string {
  if (!iso) return "";
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return "";
  return moment.toLocaleDateString("nl-NL", {
    timeZone: "Europe/Amsterdam",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** De reden in woorden; een 403 zonder tekst krijgt een eigen zin. */
function foutReden(fout: unknown): string {
  const ax = fout as AxiosError<{ detail?: unknown }>;
  const detail = ax?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (ax?.response?.status === 403) return REDEN_GEEN_BEHEERDER;
  return vertaalApiFout(fout, "Opslaan is niet gelukt.");
}

export function WaarheidskaartenTab() {
  const kaarten = useQuery({
    queryKey: KAARTEN_SLEUTEL,
    queryFn: werkplekKennisApi.waarheidskaarten,
  });
  // De namen van de vraagtypen komen uit één tabel (de stoplicht-lijst
  // van Kennis; het stoplicht zelf staat sinds sessie 53 in Beheer).
  const stoplicht = useQuery({
    queryKey: ["werkplek", "kennis", "stoplicht"],
    queryFn: werkplekKennisApi.stoplicht,
  });

  const vraagtypeNamen = new Map<string, string>();
  for (const sleutel of VASTE_VRAAGTYPEN) {
    vraagtypeNamen.set(sleutel, sleutelAlsNaam(sleutel));
  }
  for (const regel of stoplicht.data?.regels ?? []) {
    vraagtypeNamen.set(regel.vraagtype, regel.vraagtype_naam);
  }

  const lijst = kaarten.data ?? [];
  // Raskaarten (sessie 74) staan in dezelfde lijst maar werken anders; de
  // teller telt ze apart. Herkenbaar aan hun sleutel ``ras:<hond|kat>:<slug>``.
  const raskaarten = lijst.filter(isRaskaart);
  const overige = lijst.filter((kaart) => !isRaskaart(kaart));

  return (
    <div className="wp-kn-wk">
      <p className="wp-kn-uitleg wp-kn-wk-uitleg">
        Nova en Sanne gebruiken alleen goedgekeurde kaarten. Een kaart in
        concept of uit bestaat voor hen niet; staat iets op geen enkele
        goedgekeurde kaart, dan verwijzen ze naar de dierenarts of de
        voedingsdeskundige. Een kaart geldt bij zijn eigen vraagtype; een
        kaart met "Mag waarschuwen" (spoed, gif) geldt bij elk vraagtype.
      </p>
      <p className="wp-kn-uitleg wp-kn-wk-uitleg" data-testid="wp-kn-wk-uitleg-ras">
        Raskaarten werken anders: die gaan nooit vanzelf mee. Nova en Sanne
        lezen alleen de goedgekeurde raskaart van het ras waar de vraag over
        gaat, en alleen bij een voedingsvraag.
      </p>

      {kaarten.isLoading ? (
        <p className="wp-kn-stil" role="status">
          Waarheidskaarten laden…
        </p>
      ) : kaarten.isError || !kaarten.data ? (
        <p className="wp-kn-fout" role="alert">
          De waarheidskaarten zijn niet op te halen:{" "}
          {vertaalApiFout(kaarten.error, "geen antwoord van de server.")}
        </p>
      ) : (
        <>
          <p className="wp-kn-kop" data-testid="wp-kn-wk-telling">
            Waarheidskaarten: {telGoedgekeurd(overige)} van {overige.length}{" "}
            goedgekeurd
          </p>
          {raskaarten.length > 0 ? (
            <p className="wp-kn-kop" data-testid="wp-kn-wk-telling-ras">
              Raskaarten: {telGoedgekeurd(raskaarten)} van {raskaarten.length}{" "}
              goedgekeurd
            </p>
          ) : null}
          {lijst.length === 0 ? (
            <p className="wp-kn-stil">
              Er staan nog geen waarheidskaarten klaar.
            </p>
          ) : (
            <ul className="wp-kn-wk-lijst">
              {lijst.map((kaart) => (
                <KaartRij
                  key={kaart.id}
                  kaart={kaart}
                  vraagtypeNamen={vraagtypeNamen}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function isRaskaart(kaart: Waarheidskaart): boolean {
  return (kaart.onderwerp ?? "").startsWith("ras:");
}

function telGoedgekeurd(kaarten: Waarheidskaart[]): number {
  return kaarten.filter((kaart) => kaart.status === "goedgekeurd").length;
}

function KaartRij({
  kaart,
  vraagtypeNamen,
}: {
  kaart: Waarheidskaart;
  vraagtypeNamen: Map<string, string>;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [bewerken, setBewerken] = useState(false);
  const inhoudId = useId();

  const wijzigen = useMutation({
    mutationFn: (wijziging: WaarheidskaartWijziging) =>
      werkplekKennisApi.wijzigWaarheidskaart(kaart.id, wijziging),
    onSuccess: async (nieuw) => {
      // Meteen de stand uit de database laten zien, dan pas verversen.
      queryClient.setQueryData<Waarheidskaart[]>(KAARTEN_SLEUTEL, (oud) =>
        oud?.map((rij) => (rij.id === nieuw.id ? nieuw : rij)),
      );
      setBewerken(false);
      await queryClient.invalidateQueries({ queryKey: KAARTEN_SLEUTEL });
    },
  });

  const vraagtype = kaart.vraagtype
    ? (vraagtypeNamen.get(kaart.vraagtype) ?? sleutelAlsNaam(kaart.vraagtype))
    : "Geen vraagtype";
  const mag = kaart.mag ? (MAG_LABEL[kaart.mag] ?? kaart.mag) : "Mag: onbekend";
  const datum = datumLang(kaart.bijgewerkt_op ?? kaart.aangemaakt_op);

  return (
    <li className="wp-kn-kaart wp-kn-wk-kaart" data-kaart-status={kaart.status}>
      <button
        type="button"
        className="wp-kn-wk-kop"
        aria-expanded={open}
        aria-controls={inhoudId}
        onClick={() => setOpen((waarde) => !waarde)}
      >
        <span className={STATUSCHIP[kaart.status] ?? STATUSCHIP.uit}>
          {kaart.status}
        </span>
        <span className="wp-kn-kaart-titel">{kaart.titel}</span>
      </button>
      <p className="wp-kn-kaart-waarom wp-kn-wk-meta">
        {vraagtype} · {mag}
        {datum ? ` · bijgewerkt ${datum}` : ""}
      </p>

      {open ? (
        <div id={inhoudId} className="wp-kn-wk-inhoud">
          {bewerken ? (
            <KaartBewerken
              kaart={kaart}
              vraagtypeNamen={vraagtypeNamen}
              bezig={wijzigen.isPending}
              onOpslaan={(wijziging) => wijzigen.mutate(wijziging)}
              onAnnuleren={() => {
                wijzigen.reset();
                setBewerken(false);
              }}
            />
          ) : (
            <>
              <p className="wp-kn-wk-label">Klanttekst</p>
              <p className="wp-kn-wk-klanttekst">{kaart.klanttekst}</p>

              <details className="wp-kn-wk-onderbouwing">
                <summary>Onderbouwing (intern)</summary>
                {kaart.onderbouwing ? (
                  <pre className="wp-kn-wk-pre">{kaart.onderbouwing}</pre>
                ) : (
                  <p className="wp-kn-stil">Geen onderbouwing vastgelegd.</p>
                )}
              </details>

              <div className="wp-kn-kaart-rij">
                {kaart.status === "goedgekeurd" ? (
                  <button
                    type="button"
                    className="wp-knop"
                    disabled={wijzigen.isPending}
                    onClick={() => wijzigen.mutate({ status: "uit" })}
                  >
                    Uitzetten
                  </button>
                ) : (
                  <button
                    type="button"
                    className="wp-knop wp-knop-primair"
                    disabled={wijzigen.isPending}
                    onClick={() => wijzigen.mutate({ status: "goedgekeurd" })}
                  >
                    Goedkeuren
                  </button>
                )}
                <button
                  type="button"
                  className="wp-knop"
                  disabled={wijzigen.isPending}
                  onClick={() => {
                    wijzigen.reset();
                    setBewerken(true);
                  }}
                >
                  Bewerken
                </button>
              </div>
            </>
          )}

          {wijzigen.isError ? (
            <p className="wp-kn-fout wp-kn-wk-fout" role="alert">
              {foutReden(wijzigen.error)}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function KaartBewerken({
  kaart,
  vraagtypeNamen,
  bezig,
  onOpslaan,
  onAnnuleren,
}: {
  kaart: Waarheidskaart;
  vraagtypeNamen: Map<string, string>;
  bezig: boolean;
  onOpslaan: (wijziging: WaarheidskaartWijziging) => void;
  onAnnuleren: () => void;
}) {
  const id = useId();
  const [titel, setTitel] = useState(kaart.titel);
  const [klanttekst, setKlanttekst] = useState(kaart.klanttekst);
  const [onderbouwing, setOnderbouwing] = useState(kaart.onderbouwing ?? "");
  const [vraagtype, setVraagtype] = useState(kaart.vraagtype ?? "");
  const [mag, setMag] = useState<string>(kaart.mag ?? "");

  // De huidige waarde staat altijd in de lijst, ook als het stoplicht
  // hem (nog) niet kent.
  const vraagtypen = new Map(vraagtypeNamen);
  if (kaart.vraagtype && !vraagtypen.has(kaart.vraagtype)) {
    vraagtypen.set(kaart.vraagtype, sleutelAlsNaam(kaart.vraagtype));
  }

  const wijziging: WaarheidskaartWijziging = {};
  if (titel.trim() !== kaart.titel) wijziging.titel = titel.trim();
  if (klanttekst !== kaart.klanttekst) wijziging.klanttekst = klanttekst;
  if (onderbouwing !== (kaart.onderbouwing ?? "")) {
    wijziging.onderbouwing = onderbouwing;
  }
  if (vraagtype && vraagtype !== (kaart.vraagtype ?? "")) {
    wijziging.vraagtype = vraagtype;
  }
  if (mag && mag !== (kaart.mag ?? "")) {
    wijziging.mag = mag as WaarheidskaartMag;
  }
  const leeg = !titel.trim() || !klanttekst.trim();
  const ongewijzigd = Object.keys(wijziging).length === 0;
  // De server zet een goedgekeurde kaart terug op concept als er iets
  // verandert dat Nova en Sanne zien; alleen de onderbouwing laat hem staan.
  const terugNaarConcept =
    kaart.status === "goedgekeurd" &&
    ["titel", "klanttekst", "vraagtype", "mag"].some((veld) => veld in wijziging);
  const reden = leeg
    ? "Titel en klanttekst mogen niet leeg zijn."
    : ongewijzigd
      ? "Er is nog niets gewijzigd."
      : null;

  return (
    <form
      className="wp-kn-wk-form"
      onSubmit={(gebeurtenis) => {
        gebeurtenis.preventDefault();
        if (!reden) onOpslaan(wijziging);
      }}
    >
      {terugNaarConcept ? (
        <p className="wp-kn-stil" data-testid="wp-kn-wk-terug-concept">
          Na opslaan staat deze kaart weer op concept. Nova en Sanne gebruiken hem pas
          weer als je hem opnieuw goedkeurt.
        </p>
      ) : null}
      <label htmlFor={`${id}-titel`}>Titel</label>
      <input
        id={`${id}-titel`}
        value={titel}
        onChange={(e) => setTitel(e.target.value)}
      />

      <label htmlFor={`${id}-klanttekst`}>Klanttekst</label>
      <textarea
        id={`${id}-klanttekst`}
        rows={6}
        value={klanttekst}
        onChange={(e) => setKlanttekst(e.target.value)}
      />

      <label htmlFor={`${id}-onderbouwing`}>Onderbouwing (intern)</label>
      <textarea
        id={`${id}-onderbouwing`}
        className="wp-kn-wk-mono"
        rows={8}
        value={onderbouwing}
        onChange={(e) => setOnderbouwing(e.target.value)}
      />

      <div className="wp-kn-wk-twee">
        <div>
          <label htmlFor={`${id}-vraagtype`}>Vraagtype</label>
          <select
            id={`${id}-vraagtype`}
            value={vraagtype}
            onChange={(e) => setVraagtype(e.target.value)}
          >
            {!kaart.vraagtype ? <option value="">Kies een vraagtype</option> : null}
            {[...vraagtypen.entries()].map(([sleutel, naam]) => (
              <option key={sleutel} value={sleutel}>
                {naam}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${id}-mag`}>Mag</label>
          <select
            id={`${id}-mag`}
            value={mag}
            onChange={(e) => setMag(e.target.value)}
          >
            {!kaart.mag ? <option value="">Kies wat mag</option> : null}
            {MAG_OPTIES.map((optie) => (
              <option key={optie.waarde} value={optie.waarde}>
                {optie.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="wp-kn-kaart-rij">
        <button
          type="submit"
          className="wp-knop wp-knop-primair"
          disabled={bezig || reden !== null}
        >
          Opslaan
        </button>
        <button
          type="button"
          className="wp-knop"
          disabled={bezig}
          onClick={onAnnuleren}
        >
          Annuleren
        </button>
        {/* Uitgeschakeld is uitleggen waarom. */}
        {reden ? <span className="wp-kn-wk-reden">{reden}</span> : null}
      </div>
    </form>
  );
}
